import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";

export default function PermitToWork() {
  const { data, siteNames } = useHSE();
  const rows = data.ptws || [];
  const stats = ["Approved", "In Progress", "Closed", "Expired", "Suspended", "Rejected"].map((s) => ({ s, n: rows.filter((p) => p.status === s).length }));

  const checkPTWBlocking = (record, currentData) => {
    const reasons = [];

    // 1. Check equipment expiry/quarantine
    const reqCerts = String(record.certificates || record.equipment || "").toLowerCase();
    const expiredEq = (currentData.equipment || []).find((eq) => {
      const match = reqCerts.includes(String(eq.type || "").toLowerCase()) || reqCerts.includes(String(eq.id || "").toLowerCase());
      return match && (eq.status === "Expired" || (eq.expiryDate && new Date(eq.expiryDate) < new Date()));
    });
    if (expiredEq) {
      reasons.push(`[EXPIRED_EQUIPMENT_CERTIFICATE] Equipment certificate for ${expiredEq.type || expiredEq.id} is expired. Permit blocked.`);
    }

    // 2. Check permit receiver/holder training expiry
    const receiver = String(record.receiver || "").toLowerCase();
    const expiredTrn = (currentData.trainings || []).find((t) => {
      const matchName = receiver.includes(String(t.employeeName || "").toLowerCase()) || receiver.includes(String(t.employeeId || "").toLowerCase());
      return matchName && (t.status === "Expired" || (t.expiryDate && new Date(t.expiryDate) < new Date()));
    });
    if (expiredTrn) {
      reasons.push(`[EXPIRED_MANDATORY_TRAINING] Permit holder (${record.receiver}) mandatory training ${expiredTrn.type} is expired and authorization is suspended. Permit blocked.`);
    }

    // 3. Check Linked Risk Assessment & Method Statement
    const raId = record.linkedRA;
    const msId = record.linkedMS;
    const validRA = !raId || (currentData.riskAssessments || []).some((ra) => ra.id === raId && (ra.status === "Approved" || !ra.status));
    const validMS = !msId || (currentData.methodStatements || []).some((ms) => ms.id === msId && (ms.status === "Approved" || !ms.status));
    if (!validRA || !validMS) {
      reasons.push(`[INVALID_RISK_ASSESSMENT] Linked Risk Assessment (${raId || "N/A"}) or Method Statement (${msId || "N/A"}) is missing, draft, or expired. Permit blocked.`);
    }

    return reasons;
  };

  const handleBeforeSave = (editingRecord, currentData) => {
    if (editingRecord.status === "Approved" || editingRecord.status === "In Progress") {
      const blockingReasons = checkPTWBlocking(editingRecord, currentData);
      if (blockingReasons.length > 0) {
        return blockingReasons[0];
      }
    }
    return null;
  };

  const handleSaveRecord = async (editingRecord) => {
    try {
      const res = await apiFetch("/ptws", {
        method: "POST",
        body: {
          ...editingRecord,
          ptwType: editingRecord.workType || "hot_work",
          title: editingRecord.workDescription || editingRecord.id || "PTW Record",
          permitHolderUserId: editingRecord.receiver || editingRecord.permitHolderUserId || "user-1",
          startTime: editingRecord.startTime || new Date().toISOString(),
          endTime: editingRecord.endTime || new Date(Date.now() + 8 * 3600000).toISOString(),
          idempotencyKey: `ptw-${editingRecord.id}`,
        },
      });
      return res?.ptw ? { ...editingRecord, ...res.ptw } : editingRecord;
    } catch (err) {
      // Re-throw server error if PTW_BLOCKED or server validation failure
      if (err.code === "PTW_BLOCKED" || err.status === 400) {
        throw new Error(err.message || "Permit to Work is authoritatively blocked by server rules.");
      }
      return editingRecord;
    }
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {stats.map((x) => (
        <StatCard key={x.s} label={x.s} value={x.n} tone={x.s === "Expired" ? "critical" : x.s === "Approved" ? "safe" : x.s === "In Progress" ? "info" : "neutral"} />
      ))}
    </div>
  );

  return (
    <RegisterPage
      title="Permit to Work"
      subtitle="High-risk activity permits — Hot Work · WAH · Confined Space · Excavation · Electrical · LOTO"
      storeKey="ptws"
      idPrefix="PTW"
      topPanels={Top}
      onBeforeSave={handleBeforeSave}
      onSaveRecord={handleSaveRecord}
      columns={[
        { key: "id", label: "PTW ID" },
        { key: "permitNo", label: "Permit No" },
        { key: "date", label: "Date" },
        { key: "site", label: "Site" },
        { key: "workType", label: "Work Type" },
        { key: "location", label: "Location" },
        { key: "contractor", label: "Contractor" },
        { key: "issuer", label: "Issuer" },
        { key: "receiver", label: "Receiver" },
        { key: "approver", label: "Approver" },
        { key: "linkedRA", label: "Linked RA", render: (r) => r.linkedRA ? <span className="font-mono text-[11px] text-[#082B4D]">{r.linkedRA}</span> : <span className="text-slate-300">—</span> },
        { key: "linkedMS", label: "Linked MS", render: (r) => r.linkedMS ? <span className="font-mono text-[11px] text-[#082B4D]">{r.linkedMS}</span> : <span className="text-slate-300">—</span> },
        { key: "risk", label: "Risk" },
        {
          key: "status",
          label: "Status",
          render: (r) => {
            const blocking = checkPTWBlocking(r, data);
            if (blocking.length > 0 && (r.status === "Approved" || r.status === "In Progress")) {
              return (
                <div className="flex flex-col gap-1">
                  <span className="cmd-pill bg-rose-100 text-rose-800 font-bold">Blocked</span>
                  <span className="text-[10px] text-rose-600 font-semibold">{blocking[0]}</span>
                </div>
              );
            }
            const m = STATUS_BADGES[r.status] || { bg: "#F1F5F9", color: "#455560" };
            return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{r.status}</span>;
          },
        },
      ]}
      badgeMap={{ risk: PRIORITY_BADGES }}
      formFields={[
        { key: "id", label: "PTW ID" },
        { key: "permitNo", label: "Permit Number" },
        { key: "date", label: "Date", type: "date" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "workType", label: "Work Type", type: "select", options: data.settings.ptwTypes },
        { key: "location", label: "Location" },
        { key: "contractor", label: "Contractor", type: "select", options: data.settings.contractors },
        { key: "workDescription", label: "Work Description", type: "textarea", full: true },
        { key: "issuer", label: "Permit Issuer" },
        { key: "receiver", label: "Permit Receiver" },
        { key: "approver", label: "Approver" },
        { key: "startTime", label: "Start Date/Time", type: "datetime-local" },
        { key: "endTime", label: "End Date/Time", type: "datetime-local" },
        { key: "risk", label: "Risk Level", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "linkedRA", label: "Linked Risk Assessment" },
        { key: "linkedMS", label: "Linked Method Statement" },
        { key: "certificates", label: "Required Certificates / Equipment" },
        { key: "closeoutEvidence", label: "Closeout Evidence", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["Approved", "In Progress", "Closed", "Expired", "Suspended", "Rejected"] },
      ]}
    />
  );
}
